'use client';

import { useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { DropdownMenu } from '@/shared/components/primitives/dropdown-menu';
import { GraphqlSorting } from '@/shared/constants/graphql-sorting';
import { SortMappings } from '@/shared/helpers/sort-mappings';
import { useTranslations } from 'next-intl';

type SortDropdownProps = {
  align?: 'start' | 'center' | 'end';
  includeRelevance?: boolean;
  sort?: GraphqlSorting;
};

export const SortDropdown = ({
  align = 'start',
  includeRelevance = false,
  sort,
}: SortDropdownProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();
  const t = useTranslations('Categories');

  const currentEnum: GraphqlSorting = useMemo(() => {
    const urlSort = sp?.get('sort') ?? null;
    if (urlSort) return SortMappings.getEnumFromUrl(urlSort);
    return sort ?? GraphqlSorting.NEWEST;
  }, [sp, sort]);

  const currentLabel: string = useMemo(
    () => SortMappings.getLabelFromEnum(currentEnum),
    [currentEnum],
  );

  const handleSelect = (urlValue: string) => {
    const params = new URLSearchParams(sp?.toString());
    params.delete('page');
    params.set('sort', urlValue);

    const qs = params.toString();
    const href = qs ? `${pathname}?${qs}` : pathname;

    router.replace(href, { scroll: false });
  };

  const items = SortMappings.getOptions(includeRelevance).map((o) => ({
    label: o.label,
    value: o.urlValue,
    onClick: () => handleSelect(o.urlValue),
  }));

  return (
    <div className='flex items-center'>
      <DropdownMenu
        items={items}
        ariaLabel={t('sortGames')}
        align={align}
        triggerClassName='text-sm'
        contentClassName='text-sm'
        menuTitle={
          <span className='flex items-center gap-3'>
            <span className='text-text-muted'>{t('sortBy')}:</span>
            <span className='text-text font-semibold'>{currentLabel}</span>
          </span>
        }
        withArrow
        variant='button'
      />
    </div>
  );
};
